import { type ReactNode, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Lock, SquareArrowOutUpRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
} from '@/components/ui/combobox';
import { cn } from '@/lib/utils';
import {
  type ResourceSet,
  useResourceSets,
} from '../../../IAP/hooks/resourceSetHooks';
import { ResourceRequestItem } from 'components/IAP/hooks/incidentResourceRequestHooks';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { INCIDENT } from 'components/IAP/ICSConstants';
import { useActionAssigneeDirectory } from 'components/IAP/hooks/useActionAssigneeDirectory';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import { useAors } from 'hooks/useAors';
import { NULL_VALUE } from 'constants/displayConstants';
import { selectGroupGuid } from 'slices/commonSelectors';
import { DhsResourceDetailsDialog } from './DhsResourceDetailsDialog';
import { DhsResourceTable } from './DhsResourceTable';
import { displayOrNull, formatAorName } from './dhsResourceDisplay';
import { resourceSetToRow } from './resourceSetToRow';
import type { ResourceRow } from './ResourceTabCard';
import { TransferTaconAssignmentDialog } from './TransferTaconAssignmentDialog';

function resourceSetId(rs: ResourceSet): string {
  return rs.id || rs.resource_set_id || '';
}

type LogisticsResourceItem = ResourceRequestItem & {
  tacon?: string;
  incidents?: string[];
  incident_details?: { id: string; name: string }[];
};

export type CustomResourcesDropdownParts = {
  picker: ReactNode;
  table: ReactNode;
  hasAttached: boolean;
};

/** Shared by header + rows so columns stay aligned despite ComboboxItem chrome. */
const ATTACH_ROW_GRID =
  'tw:grid tw:w-full tw:min-w-0 tw:flex-1 tw:grid-cols-[minmax(0,1.3fr)_minmax(0,1.2fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,1.1fr)_minmax(0,1fr)_4.5rem_7.5rem] tw:items-center tw:gap-x-3 tw:text-xs';

const ATTACH_ROW_PAD = 'tw:px-2 tw:pr-8';

export const CustomResourcesDropdown = ({
  arrIndex,
  logisticsItems,
  setLogisticsItems,
  reqCount,
  isDisabled,
  canTransfer: canTransferProp,
  requestCreatorUserGuid,
  requestorPosition,
  lockedResourceSetIds,
  children,
}: {
  arrIndex?: number;
  logisticsItems: ResourceRequestItem[];
  setLogisticsItems: React.Dispatch<
    React.SetStateAction<ResourceRequestItem[][]>
  >;
  reqCount?: Record<string, number>;
  isDisabled?: boolean;
  /**
   * When false, Transfer stays visible but disabled. Defaults to `!isDisabled`
   * so RequestCard keeps prior unlock behavior.
   */
  canTransfer?: boolean;
  requestCreatorUserGuid?: string | null;
  requestorPosition?: string | null;
  /** Assets (by `resource_set_id`) that stay attached and can't be removed. */
  lockedResourceSetIds?: string[];
  /** When set, caller places picker/table (e.g. order column + detail row). */
  children?: (parts: CustomResourcesDropdownParts) => ReactNode;
}) => {
  const canTransfer = canTransferProp ?? !isDisabled;
  const groupId = useSelector(selectGroupGuid);
  const incidentId = useActualIncidentId();
  const { data: resourceSets = [] } = useResourceSets();
  const { data: incidents } = useAllIncidents();
  const { data: groupMembers = [] } = useGetRostersForGroup(groupId);
  const { data: aors = [] } = useAors();
  const directory = useActionAssigneeDirectory(INCIDENT, incidentId);

  const [detailsRow, setDetailsRow] = useState<ResourceRow | null>(null);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit'>('view');
  const [transferRow, setTransferRow] = useState<ResourceRow | null>(null);

  const resourceById = useMemo(() => {
    const map = new Map<string, ResourceSet>();
    for (const rs of resourceSets) {
      const id = resourceSetId(rs);
      if (id) map.set(id, rs);
      if (rs.resource_set_id) map.set(rs.resource_set_id, rs);
    }
    return map;
  }, [resourceSets]);

  const items = useMemo(
    () => resourceSets.map(resourceSetId).filter(Boolean),
    [resourceSets]
  );

  const selectedIds = useMemo(
    () =>
      (logisticsItems || [])
        .map((item) => {
          const rs =
            resourceById.get(item.id || '') ??
            resourceById.get(item.resource_set_id || '');
          return rs ? resourceSetId(rs) : '';
        })
        .filter(Boolean),
    [logisticsItems, resourceById]
  );

  const isLocked = (id: string) => {
    if (!lockedResourceSetIds?.length) return false;
    const rs = resourceById.get(id);
    return lockedResourceSetIds.some(
      (lockedId) => lockedId === id || lockedId === rs?.resource_set_id
    );
  };

  const labelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const rs of resourceSets) {
      const id = resourceSetId(rs);
      if (id) map.set(id, rs.name || id);
    }
    return map;
  }, [resourceSets]);

  const assigneeLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of directory.users) {
      map.set(user.value, user.label);
    }
    for (const position of directory.positions) {
      map.set(position.value, position.label);
    }
    return map;
  }, [directory.positions, directory.users]);

  const attachedRows = useMemo(() => {
    const rows: ResourceRow[] = [];
    for (const item of (logisticsItems || []) as LogisticsResourceItem[]) {
      const rs =
        resourceById.get(item.id || '') ??
        resourceById.get(item.resource_set_id || '');
      if (!rs) continue;
      const row = resourceSetToRow(rs, groupMembers);
      if (item.tacon) {
        row.tacon = item.tacon;
      }
      if (item.incidents?.length) {
        row.incidents = item.incidents;
        const primaryId = item.incidents[0];
        const fromItem = item.incident_details?.[0];
        const fromCatalog = incidents?.find((inc) => inc.id === primaryId);
        row.incident = fromItem
          ? { id: fromItem.id, name: fromItem.name }
          : fromCatalog
            ? { id: fromCatalog.id, name: fromCatalog.name }
            : { id: primaryId, name: '' };
        row.incident_details = row.incident ? [row.incident] : [];
      }
      rows.push(row);
    }
    return rows;
  }, [groupMembers, incidents, logisticsItems, resourceById]);

  const openDetails = (rs: ResourceSet) => {
    setDialogMode('view');
    setDetailsRow(resourceSetToRow(rs, groupMembers));
  };

  const openDetailsRow = (row: ResourceRow) => {
    setDialogMode('view');
    setDetailsRow(row);
  };

  const closeDetails = () => {
    setDetailsRow(null);
    setDialogMode('view');
  };

  const resolveTaconLabel = (taconId: string | undefined) => {
    if (!taconId) return NULL_VALUE;
    return assigneeLabelById.get(taconId) || formatAorName(aors, taconId);
  };

  const patchLogisticsAfterTransfer = (patch: {
    resourceId: string;
    tacon: string;
    incidents: string[];
  }) => {
    const incidentName =
      incidents?.find((inc) => inc.id === patch.incidents[0])?.name || '';
    setLogisticsItems((prev) => {
      const next = [...prev];
      const index = arrIndex || 0;
      const line = [...(next[index] || [])] as LogisticsResourceItem[];
      next[index] = line.map((item) => {
        const id = item.id || item.resource_set_id || '';
        if (
          id !== patch.resourceId &&
          item.resource_set_id !== patch.resourceId
        ) {
          return item;
        }
        return {
          ...item,
          tacon: patch.tacon,
          incidents: patch.incidents,
          incident_details: patch.incidents[0]
            ? [{ id: patch.incidents[0], name: incidentName }]
            : [],
        };
      }) as ResourceRequestItem[];
      return next;
    });
    setDetailsRow((prev) => {
      if (
        !prev ||
        (prev.id !== patch.resourceId &&
          prev.resource_set_id !== patch.resourceId)
      ) {
        return prev;
      }
      return {
        ...prev,
        tacon: patch.tacon,
        incidents: patch.incidents,
        incident: patch.incidents[0]
          ? { id: patch.incidents[0], name: incidentName }
          : prev.incident,
        incident_details: patch.incidents[0]
          ? [{ id: patch.incidents[0], name: incidentName }]
          : prev.incident_details,
      };
    });
  };

  const picker = (
    <Combobox
      multiple
      items={items}
      value={selectedIds}
      disabled={isDisabled}
      itemToStringLabel={(id: string) => labelById.get(id) ?? id}
      onValueChange={(next) => {
        const picked = (next as string[]) ?? [];
        const keptLocked = selectedIds.filter(
          (id) => isLocked(id) && !picked.includes(id)
        );
        const ids = [...keptLocked, ...picked];
        const selectedResources = ids
          .map((id) => resourceById.get(id))
          .filter((rs): rs is ResourceSet => !!rs)
          .map(
            (rs) =>
              ({
                ...rs,
                id: resourceSetId(rs),
              }) as ResourceRequestItem
          );
        setLogisticsItems((prev) => {
          const newItems = [...prev];
          newItems[arrIndex || 0] = selectedResources;
          return newItems;
        });
      }}
    >
      <ComboboxChips className="tw:w-full tw:min-w-48">
        <ComboboxValue>
          {selectedIds.map((id) => {
            const rs = resourceById.get(id);
            const row =
              attachedRows.find(
                (r) => r.id === id || r.resource_set_id === id
              ) || (rs ? resourceSetToRow(rs, groupMembers) : null);
            return (
              <div
                key={id}
                className="tw:flex tw:max-w-full tw:items-center tw:gap-1"
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="tw:h-7 tw:shrink-0 tw:whitespace-nowrap tw:px-2 tw:text-xs"
                  disabled={!incidentId || !canTransfer || !row}
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (row) setTransferRow(row);
                  }}
                >
                  Transfer
                </Button>
                <ComboboxChip
                  className="tw:max-w-full tw:gap-1.5"
                  showRemove={!isLocked(id)}
                >
                  {isLocked(id) ? (
                    <Lock
                      className="tw:size-3 tw:shrink-0"
                      aria-label="Attached asset can't be removed"
                    />
                  ) : null}
                  <span className="tw:truncate">{labelById.get(id) ?? id}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="tw:h-5 tw:shrink-0 tw:px-1 tw:text-xs"
                    aria-label={`View details for ${labelById.get(id) ?? id}`}
                    onPointerDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (rs) openDetails(rs);
                    }}
                  >
                    View Details
                    <SquareArrowOutUpRight className="tw:size-3" />
                  </Button>
                </ComboboxChip>
              </div>
            );
          })}
        </ComboboxValue>
        <ComboboxChipsInput
          placeholder="Attach resources…"
          disabled={isDisabled}
        />
      </ComboboxChips>
      <ComboboxContent
        className="dark tw:w-[min(72rem,calc(100vw-2rem))] tw:min-w-[48rem]"
        positionerClassName="tw:z-[90]"
      >
        <div
          className={cn(
            ATTACH_ROW_GRID,
            ATTACH_ROW_PAD,
            'tw:border-b tw:border-border tw:py-1.5 tw:font-medium tw:text-muted-foreground'
          )}
        >
          <div>Name</div>
          <div>Current Location</div>
          <div>OPCON</div>
          <div>TACON</div>
          <div>In Incident</div>
          <div>In Request</div>
          <div>Quantity</div>
          <div />
        </div>
        <ComboboxEmpty>No organization resources</ComboboxEmpty>
        <ComboboxList>
          {(item: string) => {
            const rs = resourceById.get(item);
            if (!rs) return null;
            const inIncident =
              rs.incidents && rs.incidents.length > 0
                ? `In Use by ${
                    incidents?.find((inc) => inc.id === rs.incidents?.[0])
                      ?.name || 'Unknown'
                  }`
                : 'Available';
            const inRequest = reqCount?.[rs.resource_set_id || '']
              ? `Requested ${reqCount[rs.resource_set_id || '']} times`
              : 'Not Requested';

            return (
              <ComboboxItem
                key={item}
                value={item}
                className={cn(
                  ATTACH_ROW_PAD,
                  'tw:h-auto tw:min-h-8 tw:w-full tw:gap-0 tw:py-2'
                )}
              >
                <div className={ATTACH_ROW_GRID}>
                  <div className="tw:truncate">{rs.name}</div>
                  <div className="tw:truncate">{displayOrNull(rs.address)}</div>
                  <div className="tw:truncate">
                    {formatAorName(aors, rs.opcon)}
                  </div>
                  <div className="tw:truncate">
                    {resolveTaconLabel(rs.tacon)}
                  </div>
                  <div
                    className={
                      rs.incidents && rs.incidents.length > 0
                        ? 'tw:truncate tw:text-amber-500'
                        : 'tw:truncate tw:text-emerald-500'
                    }
                  >
                    {inIncident}
                  </div>
                  <div
                    className={
                      reqCount?.[rs.resource_set_id || '']
                        ? 'tw:truncate tw:text-amber-500'
                        : 'tw:truncate tw:text-emerald-500'
                    }
                  >
                    {inRequest}
                  </div>
                  <div className="tw:tabular-nums">QTY:{rs.quantity}</div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="tw:h-7 tw:justify-self-end tw:px-2 tw:text-xs"
                    onPointerDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      openDetails(rs);
                    }}
                  >
                    View Details
                    <SquareArrowOutUpRight className="tw:size-3.5" />
                  </Button>
                </div>
              </ComboboxItem>
            );
          }}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );

  const table =
    attachedRows.length > 0 ? (
      <DhsResourceTable
        rows={attachedRows}
        onSelectRow={openDetailsRow}
        resolveTaconLabel={resolveTaconLabel}
        renderActions={(row) => (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="tw:h-7 tw:whitespace-nowrap tw:text-xs"
            disabled={!incidentId || !canTransfer}
            onClick={(event) => {
              event.stopPropagation();
              setTransferRow(row);
            }}
          >
            Transfer TACON & Assignment
          </Button>
        )}
      />
    ) : null;

  const dialogs = (
    <>
      <DhsResourceDetailsDialog
        row={detailsRow}
        open={!!detailsRow}
        onOpenChange={(open) => {
          if (!open) closeDetails();
        }}
        mode={dialogMode}
        onModeChange={setDialogMode}
        openedInEdit={false}
        groupMembers={groupMembers}
        onRowPatch={(patch) => {
          setDetailsRow((prev) => (prev ? { ...prev, ...patch } : prev));
        }}
      />
      <TransferTaconAssignmentDialog
        open={!!transferRow}
        onOpenChange={(open) => {
          if (!open) setTransferRow(null);
        }}
        row={transferRow}
        incidentId={incidentId}
        requestCreatorUserGuid={requestCreatorUserGuid}
        requestorPosition={requestorPosition}
        onTransferred={patchLogisticsAfterTransfer}
      />
    </>
  );

  const hasAttached = attachedRows.length > 0;

  if (children) {
    return (
      <>
        {children({ picker, table, hasAttached })}
        {dialogs}
      </>
    );
  }

  return (
    <>
      {picker}
      {hasAttached ? <div className="tw:mt-2">{table}</div> : null}
      {dialogs}
    </>
  );
};
